import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { TextInput, Button, Checkbox, ActionIcon } from '@mantine/core';
import { IconSearch, IconX, IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { User } from '@turizi/shared';
import { useUpdateUser } from '@/hooks/api-hooks';
import { MENU_PERMISSIONS_LIST } from '@/components/left-menu';

export interface UserMenuPermissionsTabProps {
    user?: User;
}

export function UserMenuPermissionsTab({ user: propUser }: UserMenuPermissionsTabProps) {
    const { t } = useTranslation();
    const outletContext = useOutletContext<{ user?: User } | null>();
    const user = propUser || outletContext?.user;

    const updateUserMutation = useUpdateUser();
    const [permissions, setPermissions] = useState<Record<string, any>>(user?.permissions || {});
    const [searchMenuQuery, setSearchMenuQuery] = useState('');

    useEffect(() => {
        if (user?.permissions) {
            setPermissions(user.permissions);
        }
    }, [user?.permissions]);

    if (!user) {
        return null;
    }

    const isAllowed = (key: string) => permissions[key] !== false;

    const togglePermission = (key: string) => {
        const newValue = !isAllowed(key);
        setPermissions((prev) => ({
            ...prev,
            [key]: newValue,
        }));
    };

    const handleSelectAll = () => {
        const updated = { ...permissions };
        MENU_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = true;
        });
        setPermissions(updated);
    };

    const handleDeselectAll = () => {
        const updated = { ...permissions };
        MENU_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = false;
        });
        setPermissions(updated);
    };

    const handleSave = async () => {
        const { error } = await updateUserMutation.mutateAsync({
            id: Number(user.id),
            permissions,
        });

        if (error) {
            notifications.show({
                title: t('error'),
                message: typeof error === 'string' ? error : error.message || t('unknown_error'),
                color: 'red',
            });
        } else {
            notifications.show({
                title: t('success'),
                message: t('user_updated_successfully'),
                color: 'green',
            });
        }
    };

    const filteredMenuItems = MENU_PERMISSIONS_LIST.filter((item) => {
        if (!searchMenuQuery.trim()) return true;
        const translated = t(item.labelKey).toLowerCase();
        return translated.includes(searchMenuQuery.toLowerCase().trim());
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <TextInput
                    size="xs"
                    radius="md"
                    placeholder={t('search_permissions')}
                    value={searchMenuQuery}
                    onChange={(e) => setSearchMenuQuery(e.currentTarget.value)}
                    leftSection={<IconSearch size={14} className="text-gray-400" />}
                    rightSection={
                        searchMenuQuery ? (
                            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setSearchMenuQuery('')}>
                                <IconX size={12} />
                            </ActionIcon>
                        ) : null
                    }
                    className="flex-1 max-w-xs"
                />
                <div className="flex items-center gap-2">
                    <Button
                        size="xs"
                        variant="subtle"
                        color="emerald"
                        onClick={handleSelectAll}
                    >
                        {t('select_all')}
                    </Button>
                    <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={handleDeselectAll}
                    >
                        {t('unselect_all')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[440px] overflow-y-auto subtle-scrollbar p-0.5">
                {filteredMenuItems.length === 0 ? (
                    <div className="col-span-full text-xs text-neutral-400 text-center py-6">
                        {t('no_results_for_search')}
                    </div>
                ) : (
                    filteredMenuItems.map((item) => {
                        const checked = isAllowed(item.key);
                        return (
                            <div
                                key={item.key}
                                className={`flex items-center justify-between p-3 rounded-xl text-xs transition-all cursor-pointer select-none border ${
                                    checked
                                        ? 'bg-neutral-50 text-neutral-900 font-semibold border-neutral-900 shadow-2xs'
                                        : 'bg-white hover:bg-neutral-50 text-neutral-700 border-neutral-200'
                                }`}
                                onClick={() => togglePermission(item.key)}
                            >
                                <span className="pr-3 leading-relaxed">{t(item.labelKey)}</span>
                                <Checkbox
                                    size="xs"
                                    checked={checked}
                                    color="dark"
                                    readOnly
                                    className="pointer-events-none flex-shrink-0"
                                />
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex justify-end">
                <Button
                    color="black"
                    size="sm"
                    radius="md"
                    loading={updateUserMutation.isPending}
                    onClick={handleSave}
                    leftSection={<IconDeviceFloppy size={16} />}
                >
                    {t('save_changes')}
                </Button>
            </div>
        </div>
    );
}

export default UserMenuPermissionsTab;
